import { useCallback, useEffect, useMemo, useRef } from "react";
import { CellStyle, ColDef, RowClickedEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import {
	aggridHeaderClass,
	commonCodeCellStyle,
	commonNumberCellStyle,
} from "src/components/common/common";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { formatNumber } from "src/utils/number/numberUtils";
import {
	CreateCellClickHandler,
	createCellCssClassRules,
	CustomCellClickAction,
	GridCellConfig,
	useCellChangeHandler,
} from "src/utils/grid/GridHandlerContent";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { TQC2080_R01Dto, defaultTQC2080_R01Dto } from "./Dto_TQC2080_R01";
import { useTQC2080_R01Store } from "./Store_TQC2080_R01";
import { Info_TQC2080_R01 } from "./Info_TQC2080_R01";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'TQC2080_R01-grid-column-state';

const headerNames: Record<string, string> = {
	rowIndex: "",
	processNm: "공정명",
	testItem: "시험항목",
	stdRange: "기준치",
	centerVal: "중심치",
	analysisVal: "분석치",
	shortage: "부족사항",
	talkVol: "TANK용량(L)",
	remark: "비고",
	supplyQty: "보충 지시사항(KG.L)",
	secondAnal: "2차 분석",
	fieldConfirm: "현장확인",
};

const EDITABLE_COLUMNS = ["analysisVal", "remark", "supplyQty", "secondAnal", "fieldConfirm"];

interface Props {
	gridRef: React.RefObject<AgGridReact<TQC2080_R01Dto> | null>;
	analysisDt: string;
	onAnalysisDtChange: (value: Date | undefined) => void;
}

export const Table_TQC2080_R01 = ({ gridRef, analysisDt, onAnalysisDtChange }: Props) => {
	const {
		TQC2080_R01List,
		setTQC2080_R01List,
		updateTQC2080_R01List,
		originalTQC2080_R01List,
		selectedTQC2080_R01,
		setSelectedTQC2080_R01,
		initSelectedTQC2080_R01,
		columnState,
		setColumnState,
		detailScrollTop,
		setDetailScrollTop,
	} = useTQC2080_R01Store();

	const gridWrapperRef = useRef<HTMLDivElement>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: "itemNo",
		gridData: TQC2080_R01List,
		defaultGridData: defaultTQC2080_R01Dto,
		selectedRow: selectedTQC2080_R01,
		originalGridData: originalTQC2080_R01List,
		updateRow: (index, data) => updateTQC2080_R01List(Number(index), data),
	});

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['processNm', 'testItem', 'stdRange', 'centerVal', 'shortage', 'talkVol'],
		insertedDisabledCells: [''],
		commonDisabledCells: [''],

		pointerCells: ['analysisVal', 'supplyQty', 'secondAnal', 'fieldConfirm'],
		insertedPointerCells: [''],
		commonPointerCells: [''],
		alignLeftCells: ['processNm', 'testItem', 'remark'],
	}), []);

	const { disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns } = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
        const baseRules = createCellClassRules<TQC2080_R01Dto>({
            idKey: 'itemNo',
            selectedList: selectedTQC2080_R01?.itemNo != null ? [selectedTQC2080_R01.itemNo] : [],
            deletedList: [],
            disabledInsertedColumns,	disabledColumns,
            pointerInsertedColumns,		pointerColumns,
            alignLeftColumns,
        });

        return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
		
        disabledInsertedColumns, disabledColumns,         
        pointerInsertedColumns, pointerColumns,
		alignLeftColumns, createCellClassRules,
	]);

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'processNm')(params), []);

	const getAlignedCellStyle = useCallback(
		(params: any, key: keyof TQC2080_R01Dto, alignStyle: CellStyle) => ({
			...getCellStyle(params, key),
			...alignStyle,
		}),
		[getCellStyle],
	);

	const getNumberValueFormatter = useCallback((params: any) => {
		if (params.node?.rowPinned === "bottom") return "";
		if (params.value == null || params.value === "") return "";
		return formatNumber(params.value, 1);
	}, []);

	const handleCellValueChanged = useCellChangeHandler<TQC2080_R01Dto>({
		dataList: TQC2080_R01List,
		originalDataList: originalTQC2080_R01List,
		updateRow: (valueIndex, updatedData) => {
			updateTQC2080_R01List(Number(valueIndex), {
				...updatedData,
				shortage:
					Number(updatedData.analysisVal ?? 0) - Number(updatedData.centerVal ?? 0),
			});
		},
	});

	const cellEventHandlers = useDetailTableEvents({
		detailList: TQC2080_R01List as any,
		editableColumns: EDITABLE_COLUMNS,
		keyDownConfig: { enableArrowNavigation: true },
	});

	const cellClickActions: CustomCellClickAction<TQC2080_R01Dto>[] = useMemo(
		() => [
			{
				validate: (field) => EDITABLE_COLUMNS.includes(field),
				action: (e) => {
					e.api.startEditingCell({
						rowIndex: e.rowIndex!,
						colKey: e.colDef.field!,
					});
				},
			},
		],
		[],
	);

	const handleCellClick = CreateCellClickHandler(TQC2080_R01List, "itemNo", cellClickActions);

	const baseColumnDefs: ColDef<TQC2080_R01Dto>[] = useMemo(
		() => [
			...getDefaultRowIndexColumnConfig(),
			{
				headerName: headerNames.processNm, colId: "processNm", field: "processNm", width: 130,
				editable: false,
				headerClass: aggridHeaderClass,
				cellClass: "disabled-cell",
				cellStyle: (params) => getCellStyle(params, "processNm"),
			},
			{
				headerName: headerNames.testItem, colId: "testItem", field: "testItem", width: 160,
				editable: false,
				headerClass: aggridHeaderClass,
				cellClass: "disabled-cell",
				cellStyle: (params) => getCellStyle(params, "testItem"),	
			},
			{
				headerName: headerNames.stdRange, colId: "stdRange", field: "stdRange", width: 140,
				editable: false,
				headerClass: aggridHeaderClass,
				cellClass: "disabled-cell",
				cellStyle: (params) => getAlignedCellStyle(params, "stdRange", commonCodeCellStyle),	
			},
			{
				headerName: headerNames.centerVal, colId: "centerVal", field: "centerVal", width: 90,
				editable: false,
				headerClass: aggridHeaderClass,
				cellClass: "disabled-cell",
				valueFormatter: getNumberValueFormatter,
				cellStyle: (params) => getAlignedCellStyle(params, "centerVal", commonNumberCellStyle),	
			},
			{
				headerName: headerNames.analysisVal, colId: "analysisVal", field: "analysisVal", width: 90,
				editable: true,
				headerClass: aggridHeaderClass,
				valueFormatter: getNumberValueFormatter,
				cellStyle: (params) => getAlignedCellStyle(params, "analysisVal", commonNumberCellStyle),	
                pasteable: true,
			},
			{
				headerName: headerNames.shortage, colId: "shortage", field: "shortage", width: 90,
				editable: false,
				headerClass: aggridHeaderClass,
				cellClass: "disabled-cell",
				valueFormatter: getNumberValueFormatter,
				cellStyle: (params) => ({
					...getAlignedCellStyle(params, "shortage", commonNumberCellStyle),
					color:
						params.value != null && Number(params.value) < 0 ? "#EF4444" : "inherit",
				}),
			},
			{
				headerName: headerNames.talkVol, colId: "talkVol", field: "talkVol", width: 110,
				editable: false,
				headerClass: aggridHeaderClass,
				cellClass: "disabled-cell",
				valueFormatter: getNumberValueFormatter,
				cellStyle: (params) => getAlignedCellStyle(params, "talkVol", commonNumberCellStyle),
			},
			{
				headerName: headerNames.supplyQty, colId: "supplyQty", field: "supplyQty", width: 130,
				editable: true,
				headerClass: aggridHeaderClass,
				valueFormatter: getNumberValueFormatter,
				cellStyle: (params) =>
					getAlignedCellStyle(params, "supplyQty", commonNumberCellStyle),
				pasteable: true,
			},
			{
				headerName: headerNames.secondAnal, colId: "secondAnal", field: "secondAnal", width: 90,
				editable: true,
				headerClass: aggridHeaderClass,
				valueFormatter: getNumberValueFormatter,
				cellStyle: (params) =>
					getAlignedCellStyle(params, "secondAnal", commonNumberCellStyle),
				pasteable: true,
			},
			{
				headerName: headerNames.fieldConfirm, colId: "fieldConfirm", field: "fieldConfirm", width: 110,
				editable: true,
				headerClass: aggridHeaderClass,
				cellStyle: (params) => getCellStyle(params, "fieldConfirm"),
				pasteable: true,
			},
			{
				headerName: headerNames.remark, colId: "remark", field: "remark", flex: 1,
				editable: true,
				headerClass: aggridHeaderClass,
				cellStyle: (params) => getCellStyle(params, "remark"),
				pasteable: true,
			},
		],
		[
			TQC2080_R01List,
			selectedTQC2080_R01,
			getCellStyle,
			getAlignedCellStyle,
			getNumberValueFormatter,
		],
	);

	
	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<TQC2080_R01Dto>({
			baseColumnDefs: baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, columnState, settingRowIndex, headerNames]);

	const handleRowClick = useCallback(
		(e: RowClickedEvent<TQC2080_R01Dto>) => {
			if (e.node.rowPinned === "bottom") return;
			const rowData = e.data;
			if (!rowData) return;

			const isSelected = selectedTQC2080_R01?.itemNo === rowData.itemNo;
			setSelectedTQC2080_R01(isSelected ? undefined : rowData);
		},
		[selectedTQC2080_R01, setSelectedTQC2080_R01],
	);

	const pinnedBottomRow = useMemo(
		() => [
			{
				...defaultTQC2080_R01Dto,
				rowIndex: 0,
				itemNo: 0,
				processNm: `${TQC2080_R01List.length}건`,
				testItem: "",
				stdRange: "",
				centerVal: 0,
				analysisVal: 0,
				shortage: 0,
				talkVol: 0,
				supplyQty: 0,
				secondAnal: 0,
				fieldConfirm: "",
				remark: "",
			},
		],
		[TQC2080_R01List.length],
	);

	useEffect(() => { ensureSelectedRow(); }, [TQC2080_R01List, selectedTQC2080_R01]);

	return (
		<div className="h-full flex flex-col min-h-0">
			<Info_TQC2080_R01
				analysisDt={analysisDt}
				onAnalysisDtChange={onAnalysisDtChange}
			/>
			<div className="flex-1 min-h-0">
				<GridContextWrapper<TQC2080_R01Dto>
					enableCellCalculation
					gridWrapperRef={gridWrapperRef}
					gridRef={gridRef}
					paddedGridData={paddedGridData}
					setPaddedGridData={setTQC2080_R01List}
					initSelected={initSelectedTQC2080_R01}
					defaultRowData={defaultTQC2080_R01Dto}
					allSelected={() => {}}
					baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={setColumnState}
					localStorageKey={COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Excel
						ref={gridRef}
						rowData={paddedGridData}
						columnDefs={columnDefs}
						defaultColDef={cellCssDefaultColDef}
						getRowId={getRowId}
						rowClassRules={createRowClassRules<TQC2080_R01Dto>({
							idKey: "itemNo",
							selectedList: selectedTQC2080_R01?.itemNo != null ? [selectedTQC2080_R01.itemNo] : [],
							deletedList: [],
						})}
						onRowClicked={handleRowClick}
						onCellClicked={handleCellClick}
						onCellValueChanged={handleCellValueChanged}
						onCellKeyDown={cellEventHandlers.handleCellKeyDown}
						pinnedBottomRowData={pinnedBottomRow}
						columnStateKey={COLUMN_STATE_KEY}
						setColumnState={setColumnState}
						getDefaultColumnState={() => defaultColumnState}
						scrollTop={detailScrollTop}
						setScrollTop={setDetailScrollTop}
					/>
				</GridContextWrapper>
			</div>
		</div>
	);
};
